/* Stage 5 — B13 Workstage 7 (Post Completion) admin pages.

   Three surfaces (571-01 / 571-02 / 571-03) — reuse the accepted
   global admin shell.  Human labels only; no `571-xx` numbers in
   production UI.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import MarkSentExternallyControl from "./MarkSentExternallyControl";
import { usePageMeta } from "../../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// -------- shared local UI helpers --------
const Card = ({ title, testid, right, children }) => (
    <section className="border border-[#E5E2DC] rounded bg-white p-3"
             data-testid={testid}>
        <div className="flex items-center justify-between mb-2">
            <h3 className="font-manrope text-[13px] font-semibold">{title}</h3>
            {right}
        </div>
        {children}
    </section>);
const KV = ({ label, value, testid }) => (
    <div className="text-[12px]" data-testid={testid}>
        <div className="uppercase tracking-widest text-[10px] text-[#888]">{label}</div>
        <div className="text-[#333] font-medium">{value}</div>
    </div>);
const Pill = ({ tone = "neutral", children, testid }) => {
    const cls = tone === "good"     ? "bg-[#E4F0E4] text-[#2E5C2E]"
              : tone === "warn"     ? "bg-[#F9E8D3] text-[#7A4A17]"
              : tone === "bad"      ? "bg-[#F3D8D8] text-[#7A1F1F]"
              : tone === "info"     ? "bg-[#E4EEF4] text-[#284A5C]"
              : tone === "draft"    ? "bg-[#EAE6DE] text-[#5A503C]"
              : "bg-[#F3F0EA] text-[#3F3A2E]";
    return <span className={`inline-block px-2 py-0.5 rounded text-[11px] ${cls}`}
                 data-testid={testid}>{children}</span>;
};
const STATUS_TONE = {
    "Not Started": "neutral", "In Progress": "info",
    "Awaiting Client": "warn", "Awaiting Third Party": "warn",
    Complete: "good", Waived: "draft", "Carried Forward": "info",
};
function Modal({ title, onClose, testid, children }) {
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
             data-testid={testid || "modal"}>
            <div className="bg-white rounded shadow-lg w-[min(560px,92vw)] max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between border-b border-[#E5E2DC] px-4 py-2">
                    <h2 className="font-manrope text-[15px] font-semibold">{title}</h2>
                    <button type="button" onClick={onClose}
                            className="text-[13px] text-[#666]"
                            data-testid={`${testid || "modal"}-close`}>×</button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>);
}

// ============================================================================
// Post Completion Main (571-01)
// ============================================================================
export function PostCompletionPage({ projectId: propProjectId, mode = "active" } = {}) {
    const params = useParams();
    const projectId = propProjectId || params.projectId;
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [showAddCustom, setShowAddCustom] = useState(false);
    usePageMeta({ title: "Post completion · WS7 | Studio G4 Admin" });
    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws7`,
                { withCredentials: true });
            setData(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId]);
    useEffect(() => { refresh(); }, [refresh]);
    if (err) return <div data-testid="ws7-error" className="text-red-700 text-[13px]">{err}</div>;
    if (!data) return <div data-testid="ws7-loading" className="text-[13px] text-[#666]">Loading Post Completion…</div>;

    const filtered = (data.requirements || []).filter((r) => {
        if (statusFilter && r.status !== statusFilter) return false;
        if (q) {
            const s = q.toLowerCase();
            if (!(r.title || "").toLowerCase().includes(s)
                && !(r.package_code || "").toLowerCase().includes(s))
                return false;
        }
        return true;
    });
    const c = data.counts;

    // D5 Slice D — coherent mode propagation via fieldset gate.
    const readOnly = mode !== "active";
    return (
        <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0"
                  data-testid={`ws7-fieldset-mode-${mode}`}>
        {readOnly && (
            <div className="mb-3 px-3 py-2 rounded-md bg-[#F5F1EA] border border-[#E5E2DC] text-[12px] text-[#666]"
                 data-testid={`ws7-mode-banner-${mode}`}>
                {mode === "future"
                    ? "This Workstage is not yet active. Content is shown for reference only."
                    : "This Workstage is completed. Historical data shown, read-only."}
            </div>
        )}
        <div className="flex flex-col gap-4" data-testid="ws7-page">
            <header>
                <h1 className="font-manrope text-2xl font-semibold text-charcoal">
                    Post Completion
                </h1>
                <p className="text-[13px] text-[#666]">
                    Workstage 7 — reuses shared A4 Files, A5 Programme, A6 Client Actions and Attention, A7/B3.5 Communications.  No parallel systems.
                </p>
            </header>

            <Card title="Post Completion overview" testid="ws7-overview"
                  right={<Pill tone={data.closure_readiness_summary.ready ? "good" : "warn"}
                               testid="ws7-readiness-pill">
                             {data.closure_readiness_summary.ready
                                 ? "Ready to close" : `${data.closure_readiness_summary.blockers_count} blocker(s)`}
                         </Pill>}>
                <div className="grid grid-cols-4 gap-3">
                    <KV label="Total" value={c.total} testid="ws7-c-total" />
                    <KV label="Open" value={c.open} testid="ws7-c-open" />
                    <KV label="Blocking open" value={c.blocking_open} testid="ws7-c-blocking" />
                    <KV label="Overdue (derived)" value={c.overdue} testid="ws7-c-overdue" />
                    <KV label="Awaiting client" value={c.awaiting_client} testid="ws7-c-ac" />
                    <KV label="Awaiting third party" value={c.awaiting_third_party} testid="ws7-c-a3p" />
                    <KV label="Complete" value={c.complete} testid="ws7-c-complete" />
                    <KV label="Waived" value={c.waived} testid="ws7-c-waived" />
                </div>
            </Card>

            <Card title="Contributing packages" testid="ws7-contributing">
                {(data.contributing_packages || []).length === 0 ? (
                    <p className="text-[12px] text-[#666]" data-testid="ws7-no-contrib">
                        No contributing packages.
                    </p>
                ) : (
                    <ul className="flex flex-col gap-1">
                        {data.contributing_packages.map((p) => (
                            <li key={p.id} className="flex items-center justify-between border-b border-[#E5E2DC] py-1"
                                data-testid={`ws7-pkg-${p.id}`}>
                                <span className="text-[12px] font-mono">{p.package_code}</span>
                                <span className="text-[12px]">{p.package_name}</span>
                                <Pill tone="neutral">{p.status}</Pill>
                            </li>))}
                    </ul>)}
            </Card>

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <input value={q} onChange={(e) => setQ(e.target.value)}
                           placeholder="Filter by title or package"
                           className="border border-[#E5E2DC] rounded px-2 py-1 text-[13px]"
                           data-testid="ws7-filter-q" />
                    <select value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="border border-[#E5E2DC] rounded px-2 py-1 text-[13px]"
                            data-testid="ws7-filter-status">
                        <option value="">All statuses</option>
                        {Object.keys(STATUS_TONE).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <button type="button" onClick={() => setShowAddCustom(true)}
                        className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded"
                        data-testid="ws7-btn-add-custom">
                    + Add Custom Project requirement
                </button>
            </div>

            <Card title="Close-out requirements" testid="ws7-req-list">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="bg-[#F5F3EE] text-left text-[11px] uppercase tracking-widest text-[#666]">
                            <th className="px-2 py-1">Title</th>
                            <th className="px-2 py-1">Source</th>
                            <th className="px-2 py-1">Package</th>
                            <th className="px-2 py-1">Owner</th>
                            <th className="px-2 py-1">Due</th>
                            <th className="px-2 py-1">Blocking</th>
                            <th className="px-2 py-1">Client-visible</th>
                            <th className="px-2 py-1">Status</th>
                            <th className="px-2 py-1"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={9} className="px-2 py-2 text-center text-[12px] text-[#666]"
                                    data-testid="ws7-req-empty">
                                No requirements match the current filters.
                            </td></tr>)}
                        {filtered.map((r) => (
                            <tr key={r.id} className="border-t border-[#E5E2DC]"
                                data-testid={`ws7-req-row-${r.id}`}>
                                <td className="px-2 py-1 font-medium">{r.title}</td>
                                <td className="px-2 py-1">
                                    <Pill tone={r.source === "Package Template" ? "info"
                                              : r.source === "Project Adjustment" ? "warn"
                                              : "neutral"}
                                          testid={`ws7-req-source-${r.id}`}>
                                        {r.source}
                                    </Pill>
                                </td>
                                <td className="px-2 py-1 font-mono text-[11px]">{r.package_code || "—"}</td>
                                <td className="px-2 py-1 text-[11px]">{r.owner_id || "—"}</td>
                                <td className="px-2 py-1 text-[11px]">{(r.due_date || "").slice(0, 10) || "—"}</td>
                                <td className="px-2 py-1">{r.blocking ? "Yes" : "No"}</td>
                                <td className="px-2 py-1">
                                    {r.client_visibility === "client_visible" ? "Yes" : "No"}
                                </td>
                                <td className="px-2 py-1">
                                    <Pill tone={STATUS_TONE[r.status] || "neutral"}
                                          testid={`ws7-req-status-${r.id}`}>{r.status}</Pill>
                                    {r.overdue && <span className="ml-1 text-[10px] text-red-700"
                                                       data-testid={`ws7-req-overdue-${r.id}`}>OVERDUE</span>}
                                </td>
                                <td className="px-2 py-1 text-right">
                                    <Link to={`/admin/projects/${projectId}/ws7/requirements/${r.id}`}
                                          className="text-[11px] underline text-[#A8543F]"
                                          data-testid={`ws7-req-open-${r.id}`}>Open</Link>
                                </td>
                            </tr>))}
                    </tbody>
                </table>
            </Card>

            <Card title="Closure Readiness" testid="ws7-closure-readiness"
                  right={<Link to={`/admin/projects/${projectId}/ws7/closure-readiness`}
                               className="text-[12px] underline text-[#A8543F]"
                               data-testid="ws7-open-closure-readiness">Open Closure Readiness</Link>}>
                <p className="text-[12px] text-[#666]">
                    {data.closure_readiness_summary.ready
                        ? "All applicable gates are satisfied. Post Completion may be closed."
                        : `${data.closure_readiness_summary.blockers_count} closure blocker(s) remain — see Closure Readiness for reasons and drill-down.`}
                </p>
            </Card>

            {showAddCustom && (
                <AddCustomRequirementModal
                    projectId={projectId}
                    onClose={() => setShowAddCustom(false)}
                    onCreated={() => { setShowAddCustom(false); refresh(); }} />)}
        </div>
        </fieldset>);
}

function AddCustomRequirementModal({ projectId, onClose, onCreated }) {
    const [f, setF] = useState({
        title: "", description: "", blocking: false,
        waivable: false, carry_forward_permitted: false,
        due_date: "", client_visibility: "internal_only",
    });
    const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/ws7/requirements/custom`,
                { ...f, op_id: `ui-custom-${Date.now()}`,
                  due_date: f.due_date || null },
                { withCredentials: true });
            onCreated();
        } catch (e2) { setErr(e2?.response?.data?.error || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Modal title="Add Custom Project requirement" onClose={onClose}
               testid="ws7-add-custom-modal">
            <form onSubmit={submit} className="flex flex-col gap-2">
                <p className="text-[11px] text-[#666]">
                    Manual add creates a <em>Custom Project</em> requirement.
                    Do NOT use this to fabricate new contractual scope — for
                    new contractual obligations use Appointment / Addendum / Contract Change first.
                </p>
                <label className="text-[12px]">Title
                    <input required value={f.title}
                           onChange={(e) => setF({ ...f, title: e.target.value })}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="ws7-custom-title" />
                </label>
                <label className="text-[12px]">Description
                    <textarea rows={2} value={f.description}
                              onChange={(e) => setF({ ...f, description: e.target.value })}
                              className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1" />
                </label>
                <div className="grid grid-cols-2 gap-2">
                    <label className="text-[12px]">Due date
                        <input type="date" value={f.due_date}
                               onChange={(e) => setF({ ...f, due_date: e.target.value })}
                               className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1" />
                    </label>
                    <label className="text-[12px]">Client visibility
                        <select value={f.client_visibility}
                                onChange={(e) => setF({ ...f, client_visibility: e.target.value })}
                                className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1">
                            <option value="internal_only">Internal only</option>
                            <option value="client_visible">Client visible</option>
                        </select>
                    </label>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[12px]">
                    <label><input type="checkbox" checked={f.blocking}
                                  onChange={(e) => setF({ ...f, blocking: e.target.checked })}
                                  data-testid="ws7-custom-blocking" /> Blocking</label>
                    <label><input type="checkbox" checked={f.waivable}
                                  onChange={(e) => setF({ ...f, waivable: e.target.checked })}
                                  data-testid="ws7-custom-waivable" /> Waivable</label>
                    <label><input type="checkbox" checked={f.carry_forward_permitted}
                                  onChange={(e) => setF({ ...f, carry_forward_permitted: e.target.checked })}
                                  data-testid="ws7-custom-carry" /> Carry-forward</label>
                </div>
                {err && <p className="text-[12px] text-red-700"
                            data-testid="ws7-custom-err">{err}</p>}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                            className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="submit" disabled={busy}
                            className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded"
                            data-testid="ws7-custom-submit">
                        {busy ? "Adding…" : "Add requirement"}
                    </button>
                </div>
            </form>
        </Modal>);
}


// ============================================================================
// Requirement Detail (571-02)
// ============================================================================
export function RequirementDetailPage() {
    const { projectId, requirementId } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [modal, setModal] = useState(null);   // "complete"|"waive"|"carry"|"void"
    const reqLabel = data?.requirement?.title || data?.requirement?.reference || "Requirement";
    usePageMeta({ title: `${reqLabel} · WS7 | Studio G4 Admin` });
    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws7/requirements/${requirementId}`,
                { withCredentials: true });
            setData(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId, requirementId]);
    useEffect(() => { refresh(); }, [refresh]);
    if (err) return <div data-testid="req-error" className="text-red-700 text-[13px]">{err}</div>;
    if (!data) return <div data-testid="req-loading" className="text-[13px] text-[#666]">Loading…</div>;
    const r = data.requirement;
    const isTerminal = ["Complete", "Waived", "Carried Forward"].includes(r.status);
    const isVoid = r.void_state === "Void";

    const setStatus = async (newStatus) => {
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/ws7/requirements/${requirementId}/status`,
                { status: newStatus, op_id: `ui-status-${Date.now()}` },
                { withCredentials: true });
            refresh();
        } catch (e) { alert(e?.response?.data?.error || e.message); }
    };

    return (
        <div className="flex flex-col gap-4" data-testid="req-detail-page">
            <header>
                <div className="flex items-center gap-2">
                    <Link to={`/admin/projects/${projectId}/ws7`}
                          className="text-[12px] underline text-[#A8543F]"
                          data-testid="req-back">Post Completion</Link>
                    <span className="text-[12px] text-[#888]">/</span>
                    <span className="text-[12px] text-[#333]">Requirement</span>
                </div>
                <div className="flex items-center gap-3">
                    <h1 className="font-manrope text-2xl font-semibold text-charcoal">
                        {r.title}
                    </h1>
                    <Pill tone={STATUS_TONE[r.status] || "neutral"} testid="req-status">{r.status}</Pill>
                    {r.overdue && <Pill tone="bad" testid="req-overdue">OVERDUE (derived)</Pill>}
                    {isVoid && <Pill tone="bad" testid="req-void">VOID</Pill>}
                </div>
            </header>

            <Card title="Identity" testid="req-identity">
                <div className="grid grid-cols-3 gap-3">
                    <KV label="Source" value={r.source} testid="req-kv-source" />
                    <KV label="Package" value={r.package_code || "—"} testid="req-kv-pkg" />
                    <KV label="Owner" value={r.owner_id || "—"} testid="req-kv-owner" />
                    <KV label="Due" value={(r.due_date || "").slice(0, 10) || "—"} />
                    <KV label="Client visibility"
                        value={r.client_visibility === "client_visible" ? "Client visible" : "Internal only"} />
                    <KV label="Blocking" value={r.blocking ? "Yes" : "No"} testid="req-kv-blocking" />
                    <KV label="Waivable" value={r.waivable ? "Yes" : "No"} testid="req-kv-waivable" />
                    <KV label="Carry-forward permitted" value={r.carry_forward_permitted ? "Yes" : "No"}
                        testid="req-kv-carry" />
                    <KV label="Type" value={r.type || "general"} />
                </div>
                {r.description && (
                    <div className="mt-2 text-[12px] text-[#333]" data-testid="req-desc">
                        {r.description}
                    </div>)}
            </Card>

            <Card title="Provenance" testid="req-provenance">
                {r.source === "Package Template" && (
                    <div className="grid grid-cols-3 gap-3">
                        <KV label="Template version id" value={r.source_template_version_id || "—"} />
                        <KV label="Template version" value={r.source_template_version || "—"} />
                        <KV label="Template ref" value={r.source_template_ref || "—"} />
                    </div>)}
                {r.source === "Project Adjustment" && r.original_provenance && (
                    <div className="grid grid-cols-3 gap-3" data-testid="req-adjustment-provenance">
                        <KV label="Original source" value={r.original_provenance.source} />
                        <KV label="Original package"
                            value={r.original_provenance.package_code || "—"} />
                        <KV label="Original template ref"
                            value={r.original_provenance.source_template_ref || "—"} />
                        <KV label="Last adjustment reason"
                            value={(r.last_adjustment || {}).reason || "—"} />
                        <KV label="Adjusted by"
                            value={(r.last_adjustment || {}).adjusted_by || "—"} />
                        <KV label="Adjusted at"
                            value={((r.last_adjustment || {}).adjusted_at || "").slice(0, 19)} />
                    </div>)}
                {r.source === "Custom Project" && (
                    <div className="grid grid-cols-2 gap-3">
                        <KV label="Custom created by" value={r.custom_created_by || "—"} />
                        <KV label="Created at" value={(r.created_at || "").slice(0, 19)} />
                    </div>)}
            </Card>

            <Card title="Completion criteria &amp; required evidence" testid="req-criteria">
                {(r.completion_criteria || []).length === 0 && (r.required_evidence || []).length === 0 ? (
                    <p className="text-[12px] text-[#666]">No configured criteria — record completion note and rationale on Complete.</p>
                ) : (
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="text-[11px] uppercase text-[#888] mb-1">Completion criteria</div>
                            <ul className="text-[12px] list-disc pl-4">
                                {(r.completion_criteria || []).map((c, i) =>
                                    <li key={i}>{c.description || c.title || JSON.stringify(c)}</li>)}
                            </ul>
                        </div>
                        <div>
                            <div className="text-[11px] uppercase text-[#888] mb-1">Required evidence</div>
                            <ul className="text-[12px] list-disc pl-4">
                                {(r.required_evidence || []).map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        </div>
                    </div>)}
            </Card>

            <Card title="Linked shared records" testid="req-linked">
                <div className="grid grid-cols-3 gap-3 text-[12px]">
                    <KV label="Internal Tasks" value={(r.linked_task_ids || []).length} testid="req-linked-tasks" />
                    <KV label="Client Actions" value={(r.linked_client_action_ids || []).length} testid="req-linked-actions" />
                    <KV label="Files / Deliverables" value={(r.linked_file_ids || []).length} testid="req-linked-files" />
                    <KV label="Programme items" value={(r.linked_programme_item_ids || []).length} testid="req-linked-prog" />
                    <KV label="Contract Actions" value={(r.linked_contract_action_ids || []).length} testid="req-linked-contract" />
                    <KV label="Attention items" value={(r.linked_attention_ids || []).length} testid="req-linked-attention" />
                </div>
                <p className="text-[11px] text-[#666] mt-2">
                    Records are stored in their authoritative shared systems (A4/A5/A6).  This card shows lightweight links only.
                </p>
            </Card>

            {!isTerminal && !isVoid && (
                <Card title="Status &amp; resolution" testid="req-controls">
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-[11px] text-[#888] uppercase">Progress:</span>
                        {["Not Started","In Progress","Awaiting Client","Awaiting Third Party"].map((s) => (
                            <button key={s} type="button"
                                    onClick={() => setStatus(s)}
                                    className="text-[11px] border border-[#E5E2DC] rounded px-2 py-1 hover:bg-[#F5F3EE]"
                                    data-testid={`req-set-status-${s.replace(/\s+/g,"-")}`}>
                                {s}
                            </button>))}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                        <button type="button" onClick={() => setModal("complete")}
                                className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded"
                                data-testid="req-btn-complete">Complete</button>
                        <button type="button" onClick={() => setModal("waive")}
                                disabled={!r.waivable}
                                className="border border-[#A8543F] text-[#A8543F] text-[12px] px-3 py-1.5 rounded disabled:opacity-40"
                                data-testid="req-btn-waive">
                            Waive {r.waivable ? "" : "(not permitted)"}
                        </button>
                        <button type="button" onClick={() => setModal("carry")}
                                disabled={!r.carry_forward_permitted}
                                className="border border-[#A8543F] text-[#A8543F] text-[12px] px-3 py-1.5 rounded disabled:opacity-40"
                                data-testid="req-btn-carry">
                            Carry Forward {r.carry_forward_permitted ? "" : "(not permitted)"}
                        </button>
                        {r.source === "Custom Project" && (
                            <button type="button" onClick={() => setModal("void")}
                                    className="border border-red-600 text-red-700 text-[12px] px-3 py-1.5 rounded"
                                    data-testid="req-btn-void">Void</button>)}
                    </div>
                </Card>)}

            <Card title="History (append-only)" testid="req-history">
                <ul className="flex flex-col gap-1">
                    {(r.history || []).map((h, i) => (
                        <li key={i} className="text-[11px] border-b border-[#EEE9E0] py-1"
                            data-testid={`req-history-${i}`}>
                            <span className="font-mono text-[10px]">{(h.at || "").slice(0, 19)}</span>
                            <span className="ml-2 font-medium">{h.event}</span>
                            <span className="ml-2 text-[#888]">by {h.by}</span>
                            {h.reason && <span className="ml-2 text-[#333]">— {h.reason}</span>}
                        </li>))}
                </ul>
            </Card>

            {modal && (
                <ResolutionModal kind={modal}
                                 projectId={projectId} requirementId={requirementId}
                                 onClose={() => setModal(null)}
                                 onChanged={() => { setModal(null); refresh(); }} />)}
        </div>);
}

function ResolutionModal({ kind, projectId, requirementId, onClose, onChanged }) {
    const cfg = useMemo(() => {
        if (kind === "complete") return { title: "Complete requirement", ep: "complete" };
        if (kind === "waive") return { title: "Waive requirement", ep: "waive" };
        if (kind === "carry") return { title: "Carry Forward requirement", ep: "carry-forward" };
        if (kind === "void") return { title: "Void requirement", ep: "void" };
        return null;
    }, [kind]);
    const [f, setF] = useState({});
    const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
    if (!cfg) return null;
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            const body = { ...f, op_id: `ui-${kind}-${Date.now()}` };
            await axios.post(
                `${API}/admin/projects/${projectId}/ws7/requirements/${requirementId}/${cfg.ep}`,
                body, { withCredentials: true });
            onChanged();
        } catch (e2) {
            const d = e2?.response?.data;
            setErr(typeof d === "object" ? JSON.stringify(d?.detail || d) : e2.message);
        }
        finally { setBusy(false); }
    };
    const fields = () => {
        if (kind === "complete") return (
            <>
                <label className="text-[12px]">Completion note (required)
                    <textarea rows={3} required
                              value={f.completion_note || ""}
                              onChange={(e) => setF({ ...f, completion_note: e.target.value })}
                              className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                              data-testid="req-complete-note" />
                </label>
                <label className="text-[12px]">Evidence refs (comma separated)
                    <input value={f._ev || ""}
                           onChange={(e) => setF({ ...f, _ev: e.target.value,
                                                   evidence_refs: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="req-complete-evidence" />
                </label>
            </>);
        if (kind === "waive") return (
            <>
                <label className="text-[12px]">Reason (required)
                    <input required value={f.reason || ""}
                           onChange={(e) => setF({ ...f, reason: e.target.value })}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="req-waive-reason" />
                </label>
                <label className="text-[12px]">Authority / basis
                    <input value={f.authority || ""}
                           onChange={(e) => setF({ ...f, authority: e.target.value })}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="req-waive-authority" />
                </label>
                <label className="text-[12px]">Note
                    <textarea rows={2} value={f.note || ""}
                              onChange={(e) => setF({ ...f, note: e.target.value })}
                              className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1" />
                </label>
            </>);
        if (kind === "carry") return (
            <>
                <label className="text-[12px]">Destination / surviving mechanism (required)
                    <input required value={f.destination || ""}
                           onChange={(e) => setF({ ...f, destination: e.target.value })}
                           placeholder="e.g. Follow-up review 2027-01-10"
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="req-carry-destination" />
                </label>
                <label className="text-[12px]">Responsible owner (required)
                    <input required value={f.owner_id || ""}
                           onChange={(e) => setF({ ...f, owner_id: e.target.value })}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="req-carry-owner" />
                </label>
                <label className="text-[12px]">Reason (required)
                    <input required value={f.reason || ""}
                           onChange={(e) => setF({ ...f, reason: e.target.value })}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="req-carry-reason" />
                </label>
                <label className="text-[12px]">Next review date
                    <input type="date" value={f.next_review_date || ""}
                           onChange={(e) => setF({ ...f, next_review_date: e.target.value })}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1" />
                </label>
                <p className="text-[11px] text-[#666]">
                    Do NOT use WS8 / Finalised / generic Closed — the destination must remain operational after project closure.
                </p>
            </>);
        if (kind === "void") return (
            <>
                <p className="text-[11px] text-[#666]">
                    Void is for records created in error only. It is NEVER a substitute for Complete, Waived or Carried Forward.
                </p>
                <label className="text-[12px]">Reason (required)
                    <input required value={f.reason || ""}
                           onChange={(e) => setF({ ...f, reason: e.target.value })}
                           className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                           data-testid="req-void-reason" />
                </label>
            </>);
        return null;
    };
    return (
        <Modal title={cfg.title} onClose={onClose} testid={`req-${kind}-modal`}>
            <form onSubmit={submit} className="flex flex-col gap-2">
                {fields()}
                {err && <p className="text-[12px] text-red-700"
                            data-testid={`req-${kind}-err`}>{err}</p>}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                            className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="submit" disabled={busy}
                            className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded"
                            data-testid={`req-${kind}-submit`}>
                        {busy ? "Working…" : "Confirm"}
                    </button>
                </div>
            </form>
        </Modal>);
}

// ============================================================================
// Closure Readiness (571-03)
// ============================================================================
export function ClosureReadinessPage() {
    const { projectId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [showClose, setShowClose] = useState(false);
    usePageMeta({ title: "Closure readiness · WS7 | Studio G4 Admin" });
    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws7/closure-readiness`,
                { withCredentials: true });
            setData(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId]);
    useEffect(() => { refresh(); }, [refresh]);
    if (err) return <div className="text-red-700 text-[13px]" data-testid="cr-error">{err}</div>;
    if (!data) return <div className="text-[13px] text-[#666]" data-testid="cr-loading">Loading Closure Readiness…</div>;

    return (
        <div className="flex flex-col gap-4" data-testid="ws7-closure-readiness-page">
            <header>
                <div className="flex items-center gap-2">
                    <Link to={`/admin/projects/${projectId}/ws7`}
                          className="text-[12px] underline text-[#A8543F]"
                          data-testid="cr-back">Post Completion</Link>
                    <span className="text-[12px] text-[#888]">/</span>
                    <span className="text-[12px] text-[#333]">Closure Readiness</span>
                </div>
                <div className="flex items-center gap-3">
                    <h1 className="font-manrope text-2xl font-semibold text-charcoal">
                        Post Completion / Closure Readiness
                    </h1>
                    <Pill tone={data.ready ? "good" : "warn"} testid="cr-overall">
                        {data.ready ? "Ready to close" : `${data.blockers.length} blocker(s)`}
                    </Pill>
                </div>
                <p className="text-[13px] text-[#666]">
                    Terminal Project State is <strong>Closed</strong>. Closure Outcome for the normal path is <strong>Post Completion Complete</strong>. There is no Workstage 8 and no Finalised state.
                </p>
            </header>

            <Card title="Package readiness" testid="cr-package-readiness">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="bg-[#F5F3EE] text-left text-[11px] uppercase tracking-widest text-[#666]">
                            <th className="px-2 py-1">Package</th>
                            <th className="px-2 py-1">Total</th>
                            <th className="px-2 py-1">Open</th>
                            <th className="px-2 py-1">Ready</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.package_readiness.map((p) => (
                            <tr key={p.package_code} className="border-t border-[#E5E2DC]"
                                data-testid={`cr-pkg-${p.package_code}`}>
                                <td className="px-2 py-1 font-mono text-[11px]">{p.package_code}</td>
                                <td className="px-2 py-1">{p.total}</td>
                                <td className="px-2 py-1">{p.open}</td>
                                <td className="px-2 py-1">
                                    <Pill tone={p.ready ? "good" : "warn"}>{p.ready ? "Ready" : "Open"}</Pill>
                                </td>
                            </tr>))}
                    </tbody>
                </table>
            </Card>

            <Card title="Blockers (drill down to authoritative record)" testid="cr-blockers">
                {data.blockers.length === 0 && (
                    <p className="text-[12px] text-[#666]" data-testid="cr-no-blockers">
                        No blockers — closure gates are satisfied.
                    </p>)}
                <ul className="flex flex-col gap-1">
                    {data.blockers.map((b, i) => (
                        <li key={i} className="border-b border-[#E5E2DC] py-1"
                            data-testid={`cr-blocker-${i}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-[12px]">
                                    <span className="font-mono text-[11px] text-[#666]">{b.kind}</span>
                                    <span className="ml-2">{b.reason}</span>
                                </span>
                                {b.requirement_id && (
                                    <Link to={`/admin/projects/${projectId}/ws7/requirements/${b.requirement_id}`}
                                          className="text-[11px] underline text-[#A8543F]"
                                          data-testid={`cr-blocker-open-${i}`}>Open</Link>)}
                            </div>
                        </li>))}
                </ul>
            </Card>

            <Card title="Carried-forward obligations (surviving destinations)"
                  testid="cr-carried-forward">
                {data.carried_forward.length === 0 ? (
                    <p className="text-[12px] text-[#666]" data-testid="cr-no-cf">
                        No carried-forward obligations.
                    </p>
                ) : (
                    <ul>
                        {data.carried_forward.map((c) => (
                            <li key={c.id} className="border-b border-[#E5E2DC] py-1"
                                data-testid={`cr-cf-${c.id}`}>
                                <div className="text-[12px] font-medium">{c.title}</div>
                                <div className="text-[11px] text-[#666]">
                                    Destination: {c.destination || "—"} · Owner: {c.owner_id || "—"} · Next review: {(c.next_review_date || "").slice(0, 10) || "—"}
                                </div>
                            </li>))}
                    </ul>)}
            </Card>

            <Card title="Final communication position" testid="cr-comm">
                <div className="grid grid-cols-3 gap-3">
                    <KV label="Required" value={data.final_communication.required ? "Yes" : "No"} />
                    <KV label="Sent" value={data.final_communication.sent ? "Yes" : "No"} />
                    <KV label="Sent at" value={(data.final_communication.sent_at || "").slice(0, 19) || "—"} />
                </div>
                {data.final_communication.not_required_reason && (
                    <p className="text-[11px] text-[#666] mt-2">
                        Not-required reason: {data.final_communication.not_required_reason}
                    </p>)}
                {data.final_communication.send_method === "external" && (
                    <p className="text-[11px] text-[#666] mt-2" data-testid="ws7-ext-send-summary">
                        <span className="inline-flex rounded-full bg-[#F0EBE1] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#5A4B2A] mr-2">Sent externally</span>
                        on {data.final_communication.sent_externally_on}
                        {data.final_communication.sent_externally_notes && <> · {data.final_communication.sent_externally_notes}</>}
                    </p>)}
                {data.final_communication.required && !data.final_communication.sent && (
                    <div className="mt-3 border-t border-[#EEEBE4] pt-3">
                        <MarkSentExternallyControl
                            testid="ws7-mark-sent-externally"
                            endpointUrl={`${API}/admin/projects/${projectId}/ws7/final-communication`}
                            buildBody={({ sent_on, notes, op_id }) => ({
                                action: "mark_sent_externally",
                                sent_on, notes, op_id,
                            })}
                            onSuccess={async () => { if (refresh) await refresh(); }} />
                    </div>)}
            </Card>

            <Card title="Complete Post Completion &amp; Close Project" testid="cr-close-action">
                <p className="text-[12px] text-[#666]">
                    This action reuses the accepted Close Project authority.
                    Workstage 7 becomes Completed, Project State becomes <strong>Closed</strong>, Closure Outcome recorded as <strong>Post Completion Complete</strong>. There is no Workstage 8 and no Finalised state.
                </p>
                <div className="mt-2">
                    <button type="button" disabled={!data.ready}
                            onClick={() => setShowClose(true)}
                            className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded disabled:opacity-40"
                            data-testid="cr-btn-close">
                        Complete Post Completion &amp; Close Project
                    </button>
                    {!data.ready && (
                        <span className="ml-2 text-[11px] text-[#666]"
                              data-testid="cr-close-disabled-note">
                            Resolve the blockers above before closing.
                        </span>)}
                </div>
            </Card>

            {showClose && (
                <CloseProjectModal projectId={projectId}
                                   onClose={() => setShowClose(false)}
                                   onClosed={() => { setShowClose(false); refresh(); }} />)}
        </div>);
}

function CloseProjectModal({ projectId, onClose, onClosed }) {
    const [note, setNote] = useState("");
    const [notifyClient, setNotifyClient] = useState(false);
    const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            // Look up the current lifecycle version.
            const state = await axios.get(
                `${API}/admin/projects/${projectId}/state`,
                { withCredentials: true }).catch(() => null);
            const lv = state?.data?.lifecycle_version ?? 0;
            await axios.post(
                `${API}/admin/projects/${projectId}/ws7/complete-and-close`,
                { op_id: `ui-close-${Date.now()}`,
                  expected_project_lifecycle_version: lv,
                  closure_note: note.trim(),
                  closure_outcome_code: "post_completion_complete",
                  notify_client: notifyClient },
                { withCredentials: true });
            onClosed();
        } catch (e2) {
            const d = e2?.response?.data;
            setErr(typeof d === "object" ? JSON.stringify(d?.detail || d) : e2.message);
        }
        finally { setBusy(false); }
    };
    return (
        <Modal title="Complete Post Completion & Close Project" onClose={onClose}
               testid="cr-close-modal">
            <form onSubmit={submit} className="flex flex-col gap-2">
                <label className="text-[12px]">Closure note (required)
                    <textarea rows={3} required value={note}
                              onChange={(e) => setNote(e.target.value)}
                              className="mt-1 w-full border border-[#E5E2DC] rounded px-2 py-1"
                              data-testid="cr-close-note" />
                </label>
                <label className="text-[12px] flex items-center gap-2">
                    <input type="checkbox" checked={notifyClient}
                            onChange={(e) => setNotifyClient(e.target.checked)}
                            data-testid="cr-close-notify-client" />
                    Send client notification
                </label>
                {err && <p className="text-[12px] text-red-700"
                            data-testid="cr-close-err">{err}</p>}
                <div className="flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose}
                            className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="submit" disabled={busy}
                            className="bg-[#A8543F] text-white text-[12px] px-3 py-1.5 rounded"
                            data-testid="cr-close-submit">
                        {busy ? "Closing…" : "Complete & Close"}
                    </button>
                </div>
            </form>
        </Modal>);
}
